import React, { useEffect, useState } from 'react';
import { CheckCircle, AlertCircle, RefreshCw, X } from 'lucide-react';

export default function PaymentResultModal({ onRetry }) {
  const [resultState, setResultState] = useState(null); // { type: 'success' | 'failure', txnId: string, amount: string, date: string }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');

    if (paymentStatus === 'success' || paymentStatus === 'failure') {
      const txnId = params.get('txnid') || params.get('mihpayid') || `PAYU_${Date.now()}`;
      const amount = params.get('amount') || '1';

      setResultState({
        type: paymentStatus,
        txnId,
        amount,
        date: new Date().toLocaleString()
      });
    }
  }, []);

  if (!resultState) return null;

  const handleClose = () => {
    setResultState(null);
    // Clean up query params from address bar without reloading
    const cleanUrl = window.location.origin + window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);
  };

  const handleRetryPayment = () => {
    handleClose();
    if (onRetry) onRetry();
  };

  const isSuccess = resultState.type === 'success';

  return (
    <div className="modal-overlay" onClick={handleClose} style={{ zIndex: 10000, padding: '1rem' }}>
      <div
        className="glass-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '500px',
          backgroundColor: '#FFFFFF',
          padding: '0',
          borderRadius: '24px',
          overflow: 'hidden',
          boxShadow: '0 25px 60px rgba(15, 37, 55, 0.4)',
          position: 'relative',
          color: '#0F172A',
          margin: '0 auto',
          animation: 'fadeIn 0.3s ease'
        }}
      >
        {/* Modal Header */}
        <div style={{
          backgroundColor: isSuccess ? '#065F46' : '#991B1B',
          color: '#FFFFFF',
          padding: '1.25rem 1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: isSuccess ? '2px solid #34D399' : '2px solid #F87171'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
            {isSuccess ? <CheckCircle size={24} color="#34D399" /> : <AlertCircle size={24} color="#F87171" />}
            <div>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 800, margin: 0, color: '#FFFFFF' }}>
                {isSuccess ? 'Payment Successful!' : 'Payment Failed / Cancelled'}
              </h3>
              <p style={{ fontSize: '0.78rem', color: '#E2E8F0', margin: 0 }}>
                PayU Payment Gateway Transaction Status
              </p>
            </div>
          </div>

          <button
            onClick={handleClose}
            style={{
              background: 'rgba(255, 255, 255, 0.15)',
              border: 'none',
              color: '#FFFFFF',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal Body */}
        <div style={{ padding: '1.75rem 1.5rem', textAlign: 'center' }}>
          {/* Status Badge Icon */}
          <div style={{
            width: '64px',
            height: '64px',
            backgroundColor: isSuccess ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.25rem auto',
            border: isSuccess ? '2px solid #10B981' : '2px solid #EF4444'
          }}>
            {isSuccess ? <CheckCircle size={36} color="#10B981" /> : <AlertCircle size={36} color="#EF4444" />}
          </div>

          <h4 style={{ fontSize: '1.35rem', fontWeight: 800, color: isSuccess ? '#065F46' : '#991B1B', marginBottom: '0.5rem' }}>
            {isSuccess ? 'Transaction Confirmed!' : 'Payment Not Completed'}
          </h4>

          <p style={{ fontSize: '0.88rem', color: '#475569', lineHeight: 1.5, marginBottom: '1.5rem' }}>
            {isSuccess
              ? 'Thank you! Your payment token fee has been successfully verified by PayU. An admission advisor will contact you shortly.'
              : 'We were unable to complete your transaction via PayU Gateway. No amount was deducted, or the transaction was cancelled.'}
          </p>

          {/* Receipt Details Card */}
          <div style={{
            backgroundColor: '#F8FAFC',
            borderRadius: '16px',
            padding: '1.25rem',
            border: '1px solid #E2E8F0',
            textAlign: 'left',
            marginBottom: '1.75rem',
            fontSize: '0.85rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.65rem' }}>
              <span style={{ color: '#64748B', fontWeight: 600 }}>Payment Gateway:</span>
              <span style={{ fontWeight: 700, color: '#0F2537' }}>PayU Secure Gateway</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.65rem' }}>
              <span style={{ color: '#64748B', fontWeight: 600 }}>Transaction Ref:</span>
              <span style={{ fontWeight: 800, color: '#0F2537', fontFamily: 'monospace' }}>{resultState.txnId}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.65rem' }}>
              <span style={{ color: '#64748B', fontWeight: 600 }}>Payment Status:</span>
              <span style={{
                fontWeight: 800,
                color: isSuccess ? '#059669' : '#DC2626',
                backgroundColor: isSuccess ? '#D1FAE5' : '#FEE2E2',
                padding: '0.15rem 0.6rem',
                borderRadius: '6px'
              }}>
                {isSuccess ? 'SUCCESS' : 'FAILED / CANCELLED'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.65rem' }}>
              <span style={{ color: '#64748B', fontWeight: 600 }}>Amount:</span>
              <span style={{ fontWeight: 800, color: '#059669', fontSize: '1rem' }}>₹{resultState.amount}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748B', fontWeight: 600 }}>Date & Time:</span>
              <span style={{ color: '#334155' }}>{resultState.date}</span>
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', flexDirection: 'column' }}>
            {!isSuccess && (
              <button
                type="button"
                onClick={handleRetryPayment}
                className="btn-primary"
                style={{ width: '100%', padding: '0.9rem', fontSize: '0.98rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}
              >
                <RefreshCw size={16} /> Retry Payment Now
              </button>
            )}

            <button
              type="button"
              onClick={handleClose}
              className={isSuccess ? 'btn-primary' : 'btn-secondary'}
              style={{ width: '100%', padding: '0.9rem', fontSize: '0.98rem' }}
            >
              {isSuccess ? 'Return to Homepage' : 'Close & Back to Home'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
